import { FC, KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Asset } from 'types/Attachments';
import {
  Checkbox,
  ContextMenu,
  DropdownMenu,
  Icon,
  IconButton,
} from '@kontentino/ui';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons/faEllipsisVertical';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import { faFolderArrowDown } from '@fortawesome/pro-regular-svg-icons/faFolderArrowDown';
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons/faSpinnerThird';
import { pickAssetThumbnail } from 'app/modules/mediaLibraryPage/utils/pickAssetThumbnail';
import { formatFileSize } from 'app/modules/mediaLibraryPage/utils/formatAssetMetadata';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';

interface Props {
  asset: Asset;
  isSelected: boolean;
  showCheckbox?: boolean;
  onSelect(asset: Asset, event: MouseEvent): void;
  onOpen(asset: Asset): void;
  onRename?(asset: Asset): void;
  onMove?(asset: Asset): void;
  onDownload?(asset: Asset): void;
  onSetAsCover?(asset: Asset): void;
  onDelete?(asset: Asset): void;
}

const AssetCard: FC<Props> = ({
  asset,
  isSelected,
  showCheckbox = true,
  onSelect,
  onOpen,
  onRename,
  onMove,
  onDownload,
  onSetAsCover,
  onDelete,
}) => {
  const { t } = useTranslation();
  const thumb = pickAssetThumbnail(asset);
  const [thumbFailed, setThumbFailed] = useState(false);
  useEffect(() => {
    setThumbFailed(false);
  }, [thumb]);
  const isProcessing = asset.status !== 'ready';

  const fileNameTailLength = 8;
  const shouldTruncateMiddle = asset.file_name.length > fileNameTailLength + 3;
  const fileNameHead = shouldTruncateMiddle
    ? asset.file_name.slice(0, -fileNameTailLength)
    : asset.file_name;
  const fileNameTail = shouldTruncateMiddle
    ? asset.file_name.slice(-fileNameTailLength)
    : '';

  const handleSetAsCover = thumb ? onSetAsCover : undefined;
  const hasDropdownActions = !!(
    onRename ||
    onMove ||
    handleSetAsCover ||
    onDelete
  );
  const hasContextMenu = !!(onDownload || hasDropdownActions);

  const handleOpen = () => onOpen(asset);
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  const dropdownItems = hasDropdownActions && (
    <DropdownMenu.Content className="tw-z-50">
      {onRename && (
        <UserPermissionGate scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}>
          <DropdownMenu.Item
            onSelect={() => onRename(asset)}
            data-cy="media-library-page_asset-card_rename"
          >
            <Icon icon={faPenToSquare} />
            {t('mediaLibrary.page.asset.contextMenu.rename')}
          </DropdownMenu.Item>
        </UserPermissionGate>
      )}
      {onMove && (
        <UserPermissionGate scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}>
          <DropdownMenu.Item
            onSelect={() => onMove(asset)}
            data-cy="media-library-page_asset-card_move"
          >
            <Icon icon={faFolderArrowDown} />
            {t('mediaLibrary.page.asset.contextMenu.move')}
          </DropdownMenu.Item>
        </UserPermissionGate>
      )}
      {handleSetAsCover && (
        <UserPermissionGate scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}>
          <DropdownMenu.Item
            onSelect={() => handleSetAsCover(asset)}
            data-cy="media-library-page_asset-card_set-cover"
          >
            <Icon icon={faImage} />
            {t('mediaLibrary.page.asset.contextMenu.setCover')}
          </DropdownMenu.Item>
        </UserPermissionGate>
      )}
      {onDelete && (
        <UserPermissionGate scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}>
          <DropdownMenu.Item
            onSelect={() => onDelete(asset)}
            data-cy="media-library-page_asset-card_delete"
          >
            <Icon icon={faTrashCan} />
            {t('mediaLibrary.page.asset.contextMenu.delete')}
          </DropdownMenu.Item>
        </UserPermissionGate>
      )}
    </DropdownMenu.Content>
  );

  const cardBody = (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      className="tw-group tw-block tw-w-full tw-cursor-pointer tw-text-left focus:tw-outline-none"
      data-cy="media-library-page_grid_asset-card"
      data-asset-id={asset.id}
    >
      <div
        className={clsx(
          'tw-relative tw-flex tw-aspect-square tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border-2 tw-bg-grayscale-10 tw-bg-clip-padding',
          {
            'tw-border-primary-100': isSelected,
            'tw-border-transparent': !isSelected,
          },
        )}
      >
        <div
          className={clsx('tw-absolute tw-left-3 tw-top-3 tw-z-10', {
            'tw-opacity-100': showCheckbox || isSelected,
            'tw-opacity-0 focus-within:tw-opacity-100 group-hover:tw-opacity-100':
              !showCheckbox && !isSelected,
          })}
          data-cy="media-library-page_asset-card_select"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(asset, e);
          }}
        >
          <Checkbox checked={isSelected} onChange={() => null} size="small" />
        </div>
        {thumb && !thumbFailed ? (
          <img
            src={thumb}
            alt={asset.file_name}
            draggable={false}
            className="tw-h-full tw-w-full tw-object-cover"
            onError={() => setThumbFailed(true)}
          />
        ) : (
          <span aria-hidden className="tw-text-grayscale-90">
            {asset.type ?? 'file'}
          </span>
        )}
        {isProcessing && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-gap-1 tw-bg-grayscale-180 tw-bg-opacity-40 tw-text-xs tw-text-white">
            <Icon icon={faSpinnerThird} className="tw-animate-spin" />
            {t('mediaLibrary.page.upload.tray.processing')}
          </div>
        )}
        {(onDownload || hasDropdownActions) && (
          <div
            className={clsx(
              'tw-absolute tw-bottom-3 tw-right-3 tw-z-10 tw-flex tw-items-center tw-gap-1 tw-transition-opacity focus-within:tw-opacity-100 group-hover:tw-opacity-100 [&:has([data-state=open])]:tw-opacity-100',
              {
                'tw-opacity-100': isSelected,
                'tw-opacity-0': !isSelected,
              },
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {onDownload && (
              <IconButton
                icon={<Icon icon={faArrowDownToLine} />}
                variant="secondary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(asset);
                }}
                aria-label={t('mediaLibrary.page.asset.contextMenu.download')}
                data-cy="media-library-page_asset-card_download-button"
              />
            )}
            {hasDropdownActions && (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <IconButton
                    icon={<Icon icon={faEllipsisVertical} />}
                    variant="secondary"
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={t('moreOptions')}
                    data-cy="media-library-page_asset-card_more-button"
                  />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>{dropdownItems}</DropdownMenu.Portal>
              </DropdownMenu.Root>
            )}
          </div>
        )}
      </div>
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-1">
        <div
          className="tw-flex tw-min-w-0 tw-text-sm tw-font-semibold tw-text-grayscale-180"
          title={asset.file_name}
        >
          <span className="tw-truncate">{fileNameHead}</span>
          {fileNameTail && (
            <span className="tw-whitespace-pre">{fileNameTail}</span>
          )}
        </div>
        <div className="tw-text-xs tw-text-grayscale-100">
          {formatFileSize(asset.file_size ?? null)}
        </div>
      </div>
    </div>
  );

  if (!hasContextMenu) return cardBody;

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger>{cardBody}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="tw-z-50">
          {onRename && (
            <UserPermissionGate
              scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}
            >
              <ContextMenu.Item
                onSelect={() => onRename(asset)}
                data-cy="media-library-page_asset-card_rename"
              >
                {t('mediaLibrary.page.asset.contextMenu.rename')}
              </ContextMenu.Item>
            </UserPermissionGate>
          )}
          {onMove && (
            <UserPermissionGate
              scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}
            >
              <ContextMenu.Item
                onSelect={() => onMove(asset)}
                data-cy="media-library-page_asset-card_move"
              >
                {t('mediaLibrary.page.asset.contextMenu.move')}
              </ContextMenu.Item>
            </UserPermissionGate>
          )}
          {onDownload && (
            <ContextMenu.Item
              onSelect={() => onDownload(asset)}
              data-cy="media-library-page_asset-card_download"
            >
              {t('mediaLibrary.page.asset.contextMenu.download')}
            </ContextMenu.Item>
          )}
          {handleSetAsCover && (
            <UserPermissionGate
              scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}
            >
              <ContextMenu.Item
                onSelect={() => handleSetAsCover(asset)}
                data-cy="media-library-page_asset-card_set-cover"
              >
                {t('mediaLibrary.page.asset.contextMenu.setCover')}
              </ContextMenu.Item>
            </UserPermissionGate>
          )}
          {onDelete && (
            <UserPermissionGate
              scopes={UserPermission.MEDIA_LIBRARY_MANAGE_MEDIA}
            >
              <ContextMenu.Item
                onSelect={() => onDelete(asset)}
                data-cy="media-library-page_asset-card_delete"
              >
                {t('mediaLibrary.page.asset.contextMenu.delete')}
              </ContextMenu.Item>
            </UserPermissionGate>
          )}
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
};

export default AssetCard;
